"use client";

import { useState, useEffect } from "react";
import { Users, Star, CheckCircle } from "lucide-react";
import DashboardOverview from "./components/overview";
import SkipRequest from "./components/skipRequest";
import Stats from "./components/stats";
import ComingSoonBadge from "../../components/CommingSoon";

const skipRequestsData = [
  {
    id: 1,
    studentName: "Aarav",
    wordEnglish: "Cat",
    wordLocal: "ಬೆಕ್ಕು",
    category: "Animals",
    timestamp: "5/26/2025, 2:48:14 PM",
    status: "pending",
    teacherNote: null,
  },
  {
    id: 2,
    studentName: "Diya",
    wordEnglish: "Elephant",
    wordLocal: "ಏನುಗು",
    category: "Animals",
    level: "2",
    timestamp: "5/26/2025, 2:48:14 PM",
    status: "pending",
    teacherNote: null,
  },
  {
    id: 3,
    studentName: "Diya",
    wordEnglish: "Elephant",
    wordLocal: "ಏನುಗು",
    category: "Animals",
    timestamp: "5/26/2025, 2:48:14 PM",
    status: "denied",
    teacherNote: "Student should try again.",
  },
  {
    id: 4,
    studentName: "Diya",
    wordEnglish: "Elephant",
    wordLocal: "ಏನುಗು",
    category: "Animals",
    timestamp: "5/26/2025, 2:48:14 PM",
    status: "approved",
    teacherNote: "Good job trying! Let's practice this word again later.",
  },
];

const TeacherDashboard = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [skipRequests, setSkipRequests] = useState([]);

  const studentsData = [
    {
      id: 1,
      name: "Aarav",
      class: 2,
      easy: 3,
      medium: 3,
      hard: 4,
      wordsSpoken: 2,
      wordsDrawn: 10,
      lastPractice: "5/26/2025",
    },
    {
      id: 2,
      name: "Diya",
      class: 2,
      easy: 3,
      medium: 4,
      hard: 3,
      wordsSpoken: 0,
      wordsDrawn: 10,
      lastPractice: "5/26/2025",
    },
  ];

  const statsData = [
    {
      icon: Users,
      value: 2,
      label: "Total Students",
      color: "from-blue-400 to-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      icon: Star,
      value: 2,
      label: "Words Spoken",
      color: "from-yellow-400 to-orange-500",
      bgColor: "bg-yellow-50",
    },
    {
      icon: CheckCircle,
      value: 20,
      label: "Words Learned",
      color: "from-green-400 to-green-600",
      bgColor: "bg-green-50",
    },
  ];

  useEffect(() => {
    setIsVisible(true);
    setSkipRequests(skipRequestsData as any);
  }, []);

  const handleSkipRequest = (requestId: any, action: any) => {
    console.log(
      `Handling skip request for ID ${requestId} with action: ${action}`
    );
    // setSkipRequests((prev) =>
    //   prev.map((request) =>
    //     request.id === requestId ? { ...request, status: action } : request
    //   )
    // );
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-orange-200 via-pink-200 to-purple-400 p-6">
      <ComingSoonBadge className="right-10" />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          className={`mb-8 transform transition-all duration-1000 ${
            isVisible
              ? "translate-y-0 opacity-100"
              : "-translate-y-10 opacity-0"
          }`}
        >
          <h1 className="text-4xl font-bold text-slate-800 mb-6">
            Teacher Dashboard
          </h1>
          {/*           
          <div className="flex gap-4 mb-6">
            <button className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
              <Cloud className="w-5 h-5" />
              Sync To Cloud
            </button>
            <button className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
              <Plus className="w-5 h-5" />
              New Vocabulary Set
            </button>
          </div> */}

          {/* Tab Navigation */}
          <div className="flex gap-8 border-b border-slate-300">
            <button
              onClick={() => setActiveTab("overview")}
              className={`pb-3 px-1 font-semibold text-lg transition-all duration-300 ${
                activeTab === "overview"
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-slate-600 hover:text-purple-500"
              }`}
            >
              Class Overview
            </button>
            <button
              onClick={() => setActiveTab("requests")}
              className={`pb-3 px-1 font-semibold text-lg transition-all duration-300 ${
                activeTab === "requests"
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-slate-600 hover:text-purple-500"
              }`}
            >
              Skip Requests (
              {
                skipRequestsData.filter((req) => req.status === "pending")
                  .length
              }
              )
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <Stats statsData={statsData} isVisible={isVisible} />

        {/* Content Based on Active Tab */}
        {activeTab === "overview" ? (
          <DashboardOverview
            studentsData={studentsData as any}
            isVisible={isVisible}
          />
        ) : (
          // Skip Requests Section
          <SkipRequest
            skipRequests={skipRequests}
            isVisible={isVisible}
            handleSkipRequest={handleSkipRequest}
          />
        )}

        {/* Add Student Button - Only show on overview tab */}
        {/* {activeTab === 'overview' && (
          <div className={`flex justify-end mt-6 transform transition-all duration-1000 delay-800 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'}`}>
            <button className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
              <Plus className="w-5 h-5" />
              Add Student
            </button>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default TeacherDashboard;
